/**
 * Session Mapper Utility
 * Converts API response to frontend ConversationSession state
 */

import { DeepAnalysisFullSession } from '../../../services/api';
import {
  ConversationSession,
  ConversationTimelineItem,
  UserInputItem,
  ClarificationRequestItem,
  ReportItem,
  FollowupQuestionItem,
  FollowupResponseItem,
  SessionStatus,
  generateTimelineId,
} from '../../../types/conversation';
import { ClarificationQuestion, ClarificationResponse } from '../../ClarificationMessage';

const TOKENS_LIMIT = 50000;

/**
 * Map API status to frontend SessionStatus
 */
export function mapApiStatusToSessionStatus(
  apiStatus: DeepAnalysisFullSession['status']
): SessionStatus {
  switch (apiStatus) {
    case 'pending':
      return 'idle';
    case 'clarification':
      return 'awaiting_clarification';
    case 'exploring':
    case 'analyzing':
    case 'synthesizing':
      return 'analyzing';
    case 'complete':
      return 'complete';
    case 'error':
      return 'error';
    default:
      return 'idle';
  }
}

/**
 * Convert API clarification question format to frontend ClarificationQuestion
 */
function mapApiQuestionToFrontend(
  apiQuestion: { question: string; options: Array<{ label: string; description: string }>; allowOther: boolean }
): ClarificationQuestion {
  return {
    question: apiQuestion.question,
    options: apiQuestion.options.map(opt => ({
      label: opt.label,
      description: opt.description,
    })),
    allowOther: apiQuestion.allowOther,
  };
}

/**
 * Convert API responses to frontend ClarificationResponse array
 */
function mapApiResponsesToFrontend(
  responses: Record<string, string> | null,
  questionCount: number
): ClarificationResponse[] | undefined {
  if (!responses) return undefined;

  const result: ClarificationResponse[] = [];

  for (let i = 0; i < questionCount; i++) {
    const responseKey = String(i);
    const response = responses[responseKey];

    if (response) {
      // Check if the response matches an option or is custom text
      result.push({
        questionIndex: i,
        selectedOption: response,
        customResponse: undefined,
      });
    }
  }

  return result.length > 0 ? result : undefined;
}

/**
 * Main mapper function: Converts DeepAnalysisFullSession to ConversationSession
 */
export function mapFullSessionToConversationSession(
  fullSession: DeepAnalysisFullSession
): ConversationSession {
  const timeline: ConversationTimelineItem[] = [];
  let currentRound = 1;

  // 1. Add user_input item from documentText
  const userInput: UserInputItem = {
    id: generateTimelineId(),
    type: 'user_input',
    createdAt: fullSession.createdAt,
    roundNumber: currentRound,
    content: fullSession.documentText,
  };
  timeline.push(userInput);

  // 2. Process clarification requests in order
  for (const clarification of fullSession.clarificationRequests) {
    // Add clarification_request item
    const clarificationItem: ClarificationRequestItem = {
      id: clarification.id,
      type: 'clarification_request',
      createdAt: clarification.createdAt,
      roundNumber: currentRound,
      requestId: clarification.requestId,
      questions: clarification.questions.map(mapApiQuestionToFrontend),
      status: clarification.status === 'responded' ? 'answered' : 'pending',
      responses: mapApiResponsesToFrontend(
        clarification.responses,
        clarification.questions.length
      ),
    };
    timeline.push(clarificationItem);

    // Increment round if clarification was answered
    if (clarification.status === 'responded') {
      currentRound++;
    }
  }

  // 3. Add report item if present
  if (fullSession.finalReport) {
    const reportItem: ReportItem = {
      id: generateTimelineId(),
      type: 'report',
      createdAt: fullSession.completedAt || fullSession.createdAt,
      roundNumber: currentRound,
      markdown: fullSession.finalReport,
    };
    timeline.push(reportItem);
  }

  // 4. Add conversation messages (follow-ups)
  for (const message of fullSession.conversationMessages) {
    if (message.role === 'user') {
      const followupQuestion: FollowupQuestionItem = {
        id: message.id,
        type: 'followup_question',
        createdAt: message.createdAt,
        roundNumber: currentRound,
        content: message.content,
      };
      timeline.push(followupQuestion);
    } else {
      const followupResponse: FollowupResponseItem = {
        id: message.id,
        type: 'followup_response',
        createdAt: message.createdAt,
        roundNumber: currentRound,
        content: message.content,
        referencedJudgments: message.referencedJudgments,
        tokensUsed: message.tokensUsed,
        isStreaming: false,
      };
      timeline.push(followupResponse);
    }
  }

  // 5. Sort timeline by createdAt
  timeline.sort((a, b) =>
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  // Calculate total tokens used (from conversation messages only, session tokens are separate)
  const conversationTokens = fullSession.conversationMessages.reduce(
    (sum, msg) => sum + (msg.tokensUsed || 0),
    0
  );

  return {
    id: fullSession.id,
    status: mapApiStatusToSessionStatus(fullSession.status),
    timeline,
    currentRound,
    tokensUsed: conversationTokens,
    tokensLimit: TOKENS_LIMIT,
    error: null,
  };
}

/**
 * Check if a session can be continued (has pending clarification or is complete)
 */
export function canContinueSession(fullSession: DeepAnalysisFullSession): boolean {
  // Can continue if complete (for follow-ups)
  if (fullSession.status === 'complete') {
    return true;
  }

  // Can continue if awaiting clarification
  if (fullSession.status === 'clarification') {
    const hasPendingClarification = fullSession.clarificationRequests.some(
      cr => cr.status === 'pending'
    );
    return hasPendingClarification;
  }

  return false;
}

/**
 * Get a human-readable status label.
 * Returns a sessionStatus i18n key for the given status.
 * Callers should pass the result through t() for display.
 */
export function getStatusLabel(status: DeepAnalysisFullSession['status']): string {
  const map: Record<string, string> = {
    pending: 'sessionStatus.pending',
    clarification: 'sessionStatus.clarification',
    exploring: 'sessionStatus.exploring',
    analyzing: 'sessionStatus.analyzing',
    synthesizing: 'sessionStatus.synthesizing',
    complete: 'sessionStatus.complete',
    error: 'sessionStatus.error',
  };
  return map[status] || 'sessionStatus.unknown';
}

/**
 * Get status badge color classes
 */
export function getStatusBadgeClasses(status: DeepAnalysisFullSession['status']): string {
  switch (status) {
    case 'complete':
      return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
    case 'error':
      return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
    case 'clarification':
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300';
    case 'exploring':
    case 'analyzing':
    case 'synthesizing':
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
    default:
      return 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400';
  }
}
